// ILLUSTRATIVE FIGURES. Not one number here is real.
//
// Every figure the dashboard renders lives in this file and nowhere else, so wiring it to a
// real source is a change to one module rather than a hunt through five screens. Each block
// below names the source it is WAITING for.
//
// The portal has no performance data at all: the asset register holds carrying values and
// encumbrance, not P&L, returns, benchmarks or alpha. AUM and weights could come from the
// register today; everything else needs a source that does not yet exist. Until then the
// screens carry a visible mark saying so — see ILLUSTRATIVE in DashboardUI.
//
// Money is AED millions here, unlike the Delta module which is AED minor units. That is a
// deliberate difference for a screen that only ever shows millions, and the one place it
// matters is fmtM.
//
// DERIVATION RUNS UPWARDS AND ONLY UPWARDS: holding → portfolio line → book → total. Nothing
// is stated at two levels. A line has no `aum` of its own because its holdings already say
// what it is worth, and a reader who taps a row to see the parts must find them adding to the
// whole they came from. The only figures typed by hand are the leaves — a holding's cost,
// value and time-to-cash; a month's closing value and the money paid in or taken out during
// it — plus the inputs that genuinely have no parts here (returns, benchmarks, the
// realisation schedule, the policy limits).
//
// Two totals in this file are DEFINED as residuals so their reconciliation cannot fail:
// opening portfolio value (closing − performance − net flows) and the liquidity ladder
// (a re-partition of the same holdings the AUM is summed from). Both carry a check beside
// them that is trivially true today and becomes load-bearing the moment a real feed replaces
// the derivation. Do not delete those checks as dead weight.

export const AS_AT = '2026-09-02';

const sum = (xs, f) => (xs || []).reduce((t, x) => t + (f(x) || 0), 0);

/** Time to cash, per holding.
 *
 * THE BAND IS A PROPERTY OF THE HOLDING, not of the book it sits in. A book is not a
 * liquidity class: the Core book holds T+2 equities beside a private credit fund with a
 * multi-year lock, and the Liquidity Pool holds overnight deposits beside settled credit.
 * Tagging the leaf is also what makes the ladder reconcile to the balance sheet without
 * anyone maintaining a second set of numbers — the ladder is a re-partition of exactly the
 * holdings the AUM is summed from, so the two cannot drift apart.
 *
 * Ordered soonest-first; the ladder is rendered in this order and the cumulative column
 * depends on it.
 */
export const LIQUIDITY_BANDS = [
  { key: 'same_day', band: 'Same day', by: 'Same day', note: 'cash, deposits and T-bills' },
  { key: 'week', band: 'Within 1 week', by: 'Within a week', note: 'listed securities at settlement' },
  { key: 'quarter', band: '1–3 months', by: 'Within 3 months', note: 'monthly and quarterly redemption windows' },
  { key: 'year', band: '3–12 months', by: 'Within a year', note: 'notice periods and annual liquidity' },
  { key: 'three_year', band: '1–3 years', by: 'Within 3 years', note: 'funds in harvest, assets being marketed' },
  { key: 'beyond', band: 'Beyond 3 years', by: 'The whole book', note: 'long-dated private assets and direct stakes' },
];

// Named here rather than at the drill-down, so a holding's band reads identically in the
// ladder and in the sheet a reader opens from a portfolio row. An unknown key resolves to
// "Unclassified", the same word the ladder uses for the bucket it would fall into.
export const bandLabel = (key) =>
  LIQUIDITY_BANDS.find((b) => b.key === key)?.band || 'Unclassified';

// The three books are Project Delta's own structure — the same Core / Liquidity / Run-off
// split the plan defines — so the dashboard and the plan cannot describe different shapes.
//
// `holdings` is [{ name, invested, value, liq }] in AED m: what was put in, what it is worth
// now, and how long it takes to turn into cash. Two money figures per holding rather than a
// value and a P&L, because P&L is a subtraction and a subtraction should be done once, in
// code.
const RAW_BOOKS = [
  {
    key: 'core',
    name: 'Core Investment Book',
    short: 'Core',
    note: 'liquid capital markets',
    tone: 'navy',
    benchmarked: true,
    basis: 'Benchmarked against a blended market index.',
    lines: [
      {
        name: 'Global Equities',
        ret: 15.3,
        bmk: 14.1,
        holdings: [
          { name: 'Microsoft Corp', invested: 470, value: 620, liq: 'week' },
          { name: 'Apple Inc', invested: 430, value: 540, liq: 'week' },
          { name: 'iShares Core MSCI World ETF', invested: 400, value: 480, liq: 'week' },
          { name: 'ASML Holding NV', invested: 300, value: 410, liq: 'week' },
          { name: 'Nestlé SA', invested: 355, value: 380, liq: 'week' },
          { name: 'Taiwan Semiconductor Mfg.', invested: 260, value: 355, liq: 'week' },
          { name: 'JPMorgan Chase & Co', invested: 290, value: 330, liq: 'week' },
          { name: 'Samsung Electronics', invested: 250, value: 305, liq: 'week' },
          { name: 'LVMH Moët Hennessy', invested: 315, value: 300, liq: 'week' },
          { name: 'Shell plc', invested: 250, value: 285, liq: 'week' },
          { name: 'UnitedHealth Group', invested: 280, value: 245, liq: 'week' },
        ],
      },
      {
        name: 'Regional Equities',
        ret: 9.4,
        bmk: 8.8,
        holdings: [
          { name: 'Emirates NBD Bank', invested: 210, value: 245, liq: 'week' },
          { name: 'First Abu Dhabi Bank', invested: 205, value: 230, liq: 'week' },
          { name: 'Saudi National Bank', invested: 168, value: 175, liq: 'week' },
          { name: 'Aldar Properties', invested: 128, value: 150, liq: 'week' },
          { name: 'ADNOC Gas plc', invested: 132, value: 140, liq: 'week' },
          { name: 'Qatar National Bank', invested: 131, value: 125, liq: 'week' },
          { name: 'Americana Restaurants', invested: 95, value: 115, liq: 'week' },
        ],
      },
      {
        name: 'Fixed Income',
        ret: 5.2,
        bmk: 4.9,
        holdings: [
          { name: 'US Treasury 4.25% 2031', invested: 402, value: 420, liq: 'week' },
          { name: 'UAE Sovereign Sukuk 2033', invested: 292, value: 310, liq: 'week' },
          { name: 'Saudi Arabia 4.50% 2030', invested: 238, value: 245, liq: 'week' },
          { name: 'Emirates NBD 5.00% 2028', invested: 178, value: 190, liq: 'week' },
          { name: 'Apple Inc 3.85% 2029', invested: 176, value: 180, liq: 'week' },
          { name: 'JPMorgan 5.10% 2032', invested: 158, value: 165, liq: 'week' },
          { name: 'EM Debt Fund (hard currency)', invested: 111, value: 130, liq: 'quarter' },
        ],
      },
      {
        name: 'Alternatives Portfolio',
        ret: 7.7,
        bmk: 6.5,
        holdings: [
          { name: 'Real Estate Income Fund', invested: 150, value: 165, liq: 'year' },
          { name: 'Infrastructure Co-investment', invested: 128, value: 140, liq: 'three_year' },
          { name: 'Gold Bullion (allocated)', invested: 108, value: 125, liq: 'week' },
          { name: 'Private Credit Fund II', invested: 86, value: 95, liq: 'three_year' },
          { name: 'Commodity Trading Programme', invested: 100, value: 95, liq: 'quarter' },
        ],
      },
      {
        name: 'Hedge Funds',
        ret: 6.9,
        bmk: 6.5,
        holdings: [
          { name: 'Global Macro Fund, Class B', invested: 132, value: 145, liq: 'quarter' },
          { name: 'Multi-Strategy Platform', invested: 118, value: 130, liq: 'quarter' },
          { name: 'Equity Market Neutral Fund', invested: 115, value: 120, liq: 'quarter' },
          { name: 'Event Driven Fund', invested: 88, value: 85, liq: 'year' },
          { name: 'Systematic Trend Programme', invested: 50, value: 60, liq: 'quarter' },
        ],
      },
    ],
  },
  {
    key: 'liquidity',
    name: 'Liquidity Pool',
    short: 'Liquidity',
    note: 'absolute return target, no benchmark',
    tone: 'teal',
    benchmarked: false,
    // Not a missing benchmark — an absent one. This book targets an absolute cash-flow and
    // yield outcome, so there is no index it is trying to beat and no alpha to compute.
    basis: 'Absolute return target, so no benchmark and no alpha.',
    lines: [
      {
        name: 'Money Market & T-Bills',
        ret: 2.9,
        holdings: [
          { name: 'US Treasury Bills, 3 month', invested: 253, value: 260, liq: 'same_day' },
          { name: 'AED Overnight Deposit', invested: 176, value: 180, liq: 'same_day' },
          { name: 'USD Government Money Fund', invested: 173, value: 180, liq: 'same_day' },
        ],
      },
      {
        name: 'Government & IG Credit',
        ret: 5.2,
        holdings: [
          { name: 'US Treasury 2 year', invested: 240, value: 250, liq: 'week' },
          { name: 'Global IG Corporate Bond Fund', invested: 228, value: 240, liq: 'week' },
          { name: 'Abu Dhabi Government 2029', invested: 208, value: 220, liq: 'week' },
          { name: 'Short-duration IG Credit ETF', invested: 168, value: 180, liq: 'week' },
        ],
      },
      {
        name: 'Dividend Equity',
        ret: 7.7,
        holdings: [
          { name: 'High Dividend Yield ETF', invested: 110, value: 120, liq: 'week' },
          { name: 'Emirates NBD, income sleeve', invested: 70, value: 75, liq: 'week' },
          { name: 'Nestlé SA, income sleeve', invested: 62, value: 65, liq: 'week' },
          { name: 'Coca-Cola Co', invested: 44, value: 50, liq: 'week' },
        ],
      },
    ],
  },
  {
    key: 'runoff',
    name: 'Run-off Portfolio',
    short: 'Run-off',
    note: 'measured on realisation, not return',
    tone: 'gold',
    benchmarked: false,
    basis: 'Measured on realisation and cash generated, not on return.',
    // No `ret`: this book is deliberately not measured on return, so the column is absent
    // rather than zero. A 0.0% would assert a flat year that nobody claimed. Its holdings
    // still carry cost and value — that is a carrying position, not a performance claim.
    lines: [
      {
        name: 'Private Equity — Funds & Co-inv.',
        holdings: [
          { name: 'Buyout Fund VII, 2018 vintage', invested: 455, value: 480, liq: 'beyond' },
          { name: 'Buyout Fund V, 2014 vintage', invested: 322, value: 310, liq: 'three_year' },
          { name: 'Growth Co-investment — Logistics', invested: 260, value: 285, liq: 'three_year' },
          { name: 'Secondaries Fund III', invested: 248, value: 260, liq: 'three_year' },
          { name: 'Venture Fund II, 2016 vintage', invested: 190, value: 175, liq: 'beyond' },
          { name: 'Co-investment — Healthcare Services', invested: 103, value: 130, liq: 'beyond' },
        ],
      },
      {
        name: 'Principal Investments',
        holdings: [
          { name: 'Regional Logistics Platform, 34%', invested: 300, value: 320, liq: 'beyond' },
          { name: 'Healthcare Clinics Group, 51%', invested: 250, value: 265, liq: 'beyond' },
          { name: 'Speciality Chemicals JV, 25%', invested: 218, value: 210, liq: 'three_year' },
          { name: 'Education Holding, 40%', invested: 171, value: 185, liq: 'three_year' },
        ],
      },
      {
        name: 'Real Estate — Funds & Co-inv.',
        holdings: [
          { name: 'Dubai Commercial Tower, freehold', invested: 232, value: 245, liq: 'beyond' },
          { name: 'Abu Dhabi Residential Portfolio', invested: 186, value: 190, liq: 'three_year' },
          { name: 'European Logistics Fund II', invested: 158, value: 165, liq: 'three_year' },
          { name: 'London Office Co-investment', invested: 135, value: 130, liq: 'three_year' },
        ],
      },
    ],
  },
];

// --- derived, so a changed figure above cannot leave a stale total below ---------------

/** A line's holdings, ready to render: P&L both ways, and each holding's share of the line.
 *
 * Sorted by current value, largest first. A shareholder opening a drill-down is looking for
 * what he owns most of; alphabetical order would bury a 620m position under a 50m one. The
 * order is fixed here rather than at each call site so the sheet and any future export
 * cannot list the same holdings differently.
 *
 * `pnlPct` is against COST, not against current value — the answer to "what did this make me"
 * is the gain over what was put in.
 */
export const holdingRows = (line) => {
  const total = sum(line.holdings, (h) => h.value);
  return (line.holdings || [])
    .map((h) => ({
      ...h,
      pnl: h.value - h.invested,
      pnlPct: h.invested ? ((h.value - h.invested) / h.invested) * 100 : null,
      weight: total ? (h.value / total) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value);
};

// Each line's aum / pnl / invested are summed from its holdings at module load, so every
// consumer downstream keeps reading `l.aum` and `l.pnl` exactly as before and cannot see a
// figure the drill-down would contradict.
export const BOOKS = RAW_BOOKS.map((b) => ({
  ...b,
  lines: b.lines.map((l) => ({
    ...l,
    aum: sum(l.holdings, (h) => h.value),
    invested: sum(l.holdings, (h) => h.invested),
    pnl: sum(l.holdings, (h) => h.value - h.invested),
  })),
}));

export const bookTotals = (b) => {
  const aum = sum(b.lines, (l) => l.aum);
  const pnl = sum(b.lines, (l) => l.pnl);
  const invested = sum(b.lines, (l) => l.invested);
  // Weighted by AUM, not a mean of the line returns — an average would let a 310m line
  // pull the book as hard as a 4,250m one.
  const hasRet = b.lines.some((l) => l.ret != null);
  const ret = hasRet ? sum(b.lines, (l) => (l.ret || 0) * l.aum) / (aum || 1) : null;
  const bmk = b.benchmarked ? sum(b.lines, (l) => (l.bmk || 0) * l.aum) / (aum || 1) : null;
  return { aum, invested, pnl, ret, bmk, alpha: ret != null && bmk != null ? ret - bmk : null };
};

export const TOTAL_AUM = sum(BOOKS, (b) => bookTotals(b).aum);
export const TOTAL_PNL = sum(BOOKS, (b) => bookTotals(b).pnl);
export const weightOf = (aum) => (TOTAL_AUM ? (aum / TOTAL_AUM) * 100 : 0);

/** A sub-portfolio's alpha, or null.
 *
 * NEVER COMPUTE ALPHA WHERE THERE IS NO BENCHMARK. `l.ret - l.bmk` on a line without a
 * benchmark yields NaN, which formats as a plausible-looking dash in some places and as
 * "NaN" in others; worse, defaulting the benchmark to 0 would report the whole return as
 * outperformance. Both are inventions. The one honest answer is that the figure does not
 * exist, so this returns null and the renderer shows an em-dash.
 *
 * Lives here rather than inline at the two call sites so there is a single place the
 * question "is there a benchmark" is ever asked of a line.
 */
export const lineAlpha = (l) => (l.ret != null && l.bmk != null ? l.ret - l.bmk : null);

/** Is there any performance figure to show at all?
 *
 * Takes a book's totals or a single line — both carry `ret` and `bmk`, so one rule answers
 * it at either depth. False for the run-off book and every line in it: measured on
 * realisation, so return, benchmark and alpha do not exist for it.
 *
 * A GROUP WITH NOTHING IN IT IS NOT RENDERED AT ALL, which is a different decision from the
 * em-dash used for a single missing cell. Three dashes side by side is a row that looks
 * broken; the basis note beside it already says the figures are not kept. The Liquidity Pool
 * keeps its dashes because that row is genuinely partial — it HAS a return, and the dash
 * against benchmark and alpha is the thing that tells a reader it is not measured against an
 * index.
 */
export const hasPerformance = (x) => x.ret != null || x.bmk != null;

/** Find a book and a line again from the keys a drill-down carries. */
export const findBook = (key) => BOOKS.find((b) => b.key === key) || null;
export const findLine = (bookKey, lineName) =>
  (findBook(bookKey)?.lines || []).find((l) => l.name === lineName) || null;

// --- what the value did, and WHY it did it -----------------------------------------------

/** Waiting on: a cash-movement source (subscriptions, capital calls, distributions).
 *
 * A PORTFOLIO'S VALUE MOVES FOR TWO DIFFERENT REASONS and they must never be added together
 * silently: performance, and money the owner paid in or took out. An earlier version of this
 * file had no flows at all, so opening value was TOTAL_AUM − TOTAL_PNL and the value line
 * rose by exactly the P&L. That is not a neutral simplification — it is an unstated assertion
 * that nothing was contributed or withdrawn all year, made by a chart, which a reader has no
 * way to detect and nobody ever claimed. Flows are stated here, non-zero, so the distinction
 * is visible on the screen rather than merely handled in principle.
 *
 * Stated per month rather than as two annual totals, because the monthly figures are the
 * leaves: the annual contribution and withdrawal totals are summed from them, and every
 * month's performance is its change in value less its flows. One table, everything else
 * derived from it — the same rule the holdings follow.
 *
 * September's value is NOT stated: it is TOTAL_AUM, filled in below, so the line cannot end
 * anywhere the headline figure does not.
 */
const TRAIL_MONTHS = [
  { month: 'Jan', aum: 12240, paidIn: 0, takenOut: 0 },
  { month: 'Feb', aum: 12180, paidIn: 0, takenOut: 65 },
  { month: 'Mar', aum: 12470, paidIn: 150, takenOut: 0 },
  { month: 'Apr', aum: 12390, paidIn: 0, takenOut: 0 },
  { month: 'May', aum: 12610, paidIn: 0, takenOut: 120 },
  { month: 'Jun', aum: 12980, paidIn: 180, takenOut: 0 },
  { month: 'Jul', aum: 13160, paidIn: 0, takenOut: 80 },
  { month: 'Aug', aum: 13290, paidIn: 90, takenOut: 0 },
  { month: 'Sep', aum: null, paidIn: 0, takenOut: 60 },
];

export const CONTRIBUTIONS = sum(TRAIL_MONTHS, (m) => m.paidIn);
export const WITHDRAWALS = sum(TRAIL_MONTHS, (m) => m.takenOut);
export const NET_FLOWS = CONTRIBUTIONS - WITHDRAWALS;

/** OPENING VALUE IS THE RESIDUAL, and that is deliberate.
 *
 * Three of the four figures in the bridge are already known and none of them is free to
 * move: closing value and performance are both summed from the holdings, and the flows are
 * stated above. Opening value is the one quantity this portal has no source for at all, so
 * it is derived from the other three — which makes the bridge close BY CONSTRUCTION rather
 * than by arithmetic that happens to work out. There is no arrangement of these figures that
 * can show a total not equal to its parts.
 *
 * When a real opening-balance series arrives, this becomes a CHECK instead of a definition:
 * state the opening figure, and BRIDGE_RECONCILES below stops being trivially true and
 * starts being the thing that catches a bad feed. Leave the check in place for that day.
 */
export const OPENING_AUM = TOTAL_AUM - TOTAL_PNL - NET_FLOWS;
export const TOTAL_MOVEMENT = TOTAL_AUM - OPENING_AUM;
export const BRIDGE_RECONCILES = OPENING_AUM + TOTAL_PNL + NET_FLOWS === TOTAL_AUM;

/** Return on opening capital. A simple approximation, and labelled as one on screen.
 *
 * With flows in the period the rigorous figure is time-weighted — money that arrived in June
 * had half a year to work and money withdrawn in February did not. That needs flow DATES
 * against a valuation series, which is exactly what this module is waiting for. Stated on
 * opening rather than closing capital so the return is not flattered by the profit it
 * generated, and never on average capital, which would silently pick a third convention.
 */
export const YTD_ON_OPENING = (TOTAL_PNL / OPENING_AUM) * 100;

/** The value trail, with each month's move split into performance and flows.
 *
 * THE LINE PLOTS PORTFOLIO VALUE, WHICH INCLUDES FLOWS — it has to, because that is what the
 * portfolio was worth on each date, and it is the same quantity as the headline figure it
 * sits under. The screen therefore says so on the chart itself, and the bridge underneath
 * separates the two causes. A line that plotted performance alone would not end at total
 * portfolio value and would be a second, competing definition of "what am I worth".
 *
 * `performance` is a month's change in value LESS its net flow, so the monthly figures sum
 * to TOTAL_PNL — not by coincidence: the moves sum to closing minus opening, the flows sum
 * to NET_FLOWS, and opening is defined as closing − P&L − flows, so the residual is exactly
 * TOTAL_PNL. Asserted in TRAIL_PERF_RECONCILES for the day the opening figure comes from a
 * real feed instead.
 */
export const AUM_TRAIL = (() => {
  let prev = OPENING_AUM;
  const rows = TRAIL_MONTHS.map((m) => {
    const aum = m.aum == null ? TOTAL_AUM : m.aum;
    const flow = m.paidIn - m.takenOut;
    const move = aum - prev;
    prev = aum;
    return { ...m, aum, flow, move, performance: move - flow };
  });
  // The opening point carries no flow of its own: it is the balance everything else moves
  // from, not a month.
  return [{ month: 'Opening', aum: OPENING_AUM, paidIn: 0, takenOut: 0, flow: 0, move: 0, performance: 0 },
    ...rows];
})();

export const TRAIL_PERF_RECONCILES =
  Math.abs(sum(AUM_TRAIL, (m) => m.performance) - TOTAL_PNL) < 0.5;

// "This month" is the same two questions again at a smaller scale, and the same trap: the
// value moved +110 while performance was +170, because 60 was taken out. Reporting the move
// as P&L would understate the month by more than a third.
const LAST = AUM_TRAIL[AUM_TRAIL.length - 1];
const PRIOR_AUM = AUM_TRAIL[AUM_TRAIL.length - 2].aum;
export const MONTH_LABEL = LAST.month;
export const PRIOR_MONTH_LABEL = AUM_TRAIL[AUM_TRAIL.length - 2].month;
export const MONTH_PNL = LAST.performance;
export const MONTH_MOVE = LAST.move;
export const MONTH_FLOW = LAST.flow;
export const MONTH_PCT = (MONTH_PNL / PRIOR_AUM) * 100;

// --- liquidity: the WHOLE book, not the Liquidity Pool ----------------------------------

/** The ladder is a re-partition of every holding in all three books by time to cash.
 *
 * IT COVERS THE ENTIRE BALANCE SHEET AND MUST. An earlier version laddered the Liquidity
 * Pool alone and summed to 3,350 against a 13,400 book — a slice presented in the shape of
 * the whole, which is the quiet kind of misstatement that is worse than saying nothing. The
 * private assets in the run-off book do not disappear because they are hard to sell; they
 * belong at the far end of the ladder where a reader can see how much of his wealth is
 * parked there.
 *
 * Reconciliation is STRUCTURAL, not arithmetic luck: every holding is placed in exactly one
 * band, so the bands sum to TOTAL_AUM by construction. The one way that can fail is a
 * holding carrying a `liq` value no band declares, and such a holding is collected into
 * LADDER_UNCLASSIFIED and rendered in the open rather than silently dropped — a band total
 * that quietly loses a position is precisely the failure this design exists to prevent.
 */
const ALL_HOLDINGS = BOOKS.flatMap((b) =>
  b.lines.flatMap((l) => l.holdings.map((h) => ({ ...h, book: b, line: l.name }))));

const BAND_KEYS = new Set(LIQUIDITY_BANDS.map((b) => b.key));

export const LIQUIDITY_LADDER = (() => {
  let running = 0;
  return LIQUIDITY_BANDS.map((band) => {
    const held = ALL_HOLDINGS.filter((h) => h.liq === band.key);
    const amount = sum(held, (h) => h.value);
    running += amount;
    return {
      ...band,
      amount,
      cumulative: running,
      pct: TOTAL_AUM ? (amount / TOTAL_AUM) * 100 : 0,
      cumulativePct: TOTAL_AUM ? (running / TOTAL_AUM) * 100 : 0,
      // Which books this band draws on, in book order, so a reader can see that the ladder
      // spans all three rather than taking it on trust.
      byBook: BOOKS
        .map((b) => ({ key: b.key, name: b.short, tone: b.tone,
          amount: sum(held.filter((h) => h.book.key === b.key), (h) => h.value) }))
        .filter((x) => x.amount > 0),
    };
  });
})();

export const LADDER_UNCLASSIFIED = sum(
  ALL_HOLDINGS.filter((h) => !BAND_KEYS.has(h.liq)), (h) => h.value,
);
export const LADDER_BANDED = sum(LIQUIDITY_LADDER, (r) => r.amount);
export const LADDER_TOTAL = LADDER_BANDED + LADDER_UNCLASSIFIED;
// Stated on screen beside the ladder so a reader can check the claim rather than trust it.
export const LADDER_RECONCILES = LADDER_TOTAL === TOTAL_AUM && LADDER_UNCLASSIFIED === 0;

const bandCum = (key) => LIQUIDITY_LADDER.find((r) => r.key === key)?.cumulative || 0;
export const SAME_DAY = bandCum('same_day');
export const WITHIN_WEEK = bandCum('week');
export const WITHIN_YEAR = bandCum('year');
// Everything the ladder never reaches inside three years — the far end, stated positively so
// it is a figure a reader can hold rather than a remainder he has to work out.
export const BEYOND_THREE_YEARS =
  LIQUIDITY_LADDER.find((r) => r.key === 'beyond')?.amount || 0;

/** Waiting on: a liquidity source, and the framework's notice period.
 *
 * DELIBERATELY NOT A BAND OF THE LADDER, and it must never be folded into one. "Available
 * without notice" is a policy term — what the framework permits to be realised and
 * distributed inside its notice period — and it is smaller than the assets that could
 * physically be sold in that time, because some of them are committed, pledged or held
 * against the liquidity floor. Setting it equal to the same-day or one-week band would
 * silently promise a shareholder money the policy does not release.
 */
export const DISTRIBUTABLE = 1240;

// --- the other three tabs ----------------------------------------------------------------

export const RUNOFF_SCHEDULE = [
  { period: '2026 H2', realised: 148, expected: 210 },
  { period: '2027 H1', realised: null, expected: 430 },
  { period: '2027 H2', realised: null, expected: 380 },
  { period: '2028 H1', realised: null, expected: 520 },
  { period: '2028 H2', realised: null, expected: 610 },
  { period: '2029 and beyond', realised: null, expected: 1200 },
];

export const RUNOFF_EXPECTED = sum(RUNOFF_SCHEDULE, (r) => r.expected);
export const RUNOFF_REALISED = sum(RUNOFF_SCHEDULE, (r) => r.realised);

// Waiting on: the Delta policy module, which already holds the real framework. These
// thresholds are illustrative stand-ins for what that module will supply.
export const POLICY_LIMITS = [
  { name: 'Single issuer', limit: 5, actual: 3.8, unit: '% of AUM' },
  { name: 'Single sector', limit: 25, actual: 21.4, unit: '% of AUM' },
  { name: 'Illiquid assets', limit: 30, actual: 25.0, unit: '% of AUM' },
  { name: 'Liquidity floor', limit: null, actual: 13.6, unit: '% of AUM' },
  { name: 'Currency exposure (non-AED/USD)', limit: 15, actual: 9.2, unit: '% of AUM' },
  { name: 'Leverage', limit: 0, actual: 0, unit: '% of AUM' },
];

/** How much of a limit is used, in one place, so the Overview's headline risk line and the
 *  Policies tab cannot disagree about what is tight.
 *
 * An unset limit is NOT a limit of zero: `used` stays null and nothing is drawn, because a
 * bar at 0% and a bar at 100% would both be assertions nobody has made.
 *
 * A limit of ZERO is a prohibition, not a divisor. Any exposure at all breaches it and none
 * is full headroom; dividing by it would give Infinity and paint the bar at whatever
 * Math.min happened to clamp it to.
 */
export const limitStatus = (p) => {
  if (p.limit == null) return { unset: true, used: null, near: false, breach: false };
  const used = p.limit === 0 ? (p.actual > 0 ? 100 : 0) : (p.actual / p.limit) * 100;
  return { unset: false, used, near: used >= 85 && used <= 100, breach: used > 100 };
};

// A "tightest limit" summary used to live here, for a risk block on the Overview. Both are
// gone: the Overview is the shareholder's four questions and a limit-headroom reading was not
// one of them. Exposures against limits are the Compliance tab's whole job — do not
// reintroduce a summary of them on the Overview in another form.
